import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRouteState } from "../../hooks/useRouteState";
import { ROUTE_TYPES } from "../constants/routeConstants";

const PlanContext = createContext();

export const usePlanContext = () => {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error("usePlanContext must be used within a PlanProvider");
  }
  return context;
};

export const PlanProvider = ({ children }) => {
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [comparedPlanId, setComparedPlanId] = useState(null);
  const { routeType, params } = useRouteState();
  const currentPlanIdRef = useRef(currentPlanId);
  const comparedPlanIdRef = useRef(comparedPlanId);

  // Keep refs in sync
  useEffect(() => {
    currentPlanIdRef.current = currentPlanId;
  }, [currentPlanId]);

  useEffect(() => {
    comparedPlanIdRef.current = comparedPlanId;
  }, [comparedPlanId]);

  const routesForDataCleaning = React.useMemo(
    () => [
      ROUTE_TYPES.PATIENTS,
      ROUTE_TYPES.HOME,
      ROUTE_TYPES.DIGITAL_TWIN,
      ROUTE_TYPES.PATIENT_DASHBOARD,
      ROUTE_TYPES.LOGIN,
      ROUTE_TYPES.NOT_FOUND,
    ],
    []
  );

  const updateCurrentPlanId = useCallback(planId => {
    setCurrentPlanId(planId);
  }, []);

  const resetCurrentPlanId = useCallback(() => {
    setCurrentPlanId(null);
  }, []);

  const updateComparedPlanId = useCallback(planId => {
    setComparedPlanId(planId);
  }, []);

  const resetComparedPlanId = useCallback(() => {
    setComparedPlanId(null);
  }, []);

  useEffect(() => {
    if (routeType && routesForDataCleaning.includes(routeType)) {
      if (currentPlanIdRef.current !== null) {
        setCurrentPlanId(null);
      }
      if (comparedPlanIdRef.current !== null) {
        setComparedPlanId(null);
      }
    }
  }, [routeType, routesForDataCleaning]);

  useEffect(() => {
    if (routeType !== ROUTE_TYPES.ALIGN) {
      return;
    }

    if (params.planId && params.planId !== currentPlanIdRef.current) {
      setCurrentPlanId(params.planId);
    }

    const nextComparedPlanId = params.compareId || null;
    if (nextComparedPlanId !== comparedPlanIdRef.current) {
      setComparedPlanId(nextComparedPlanId);
    }
  }, [routeType, params.planId, params.compareId]);

  const value = {
    currentPlanId,
    updateCurrentPlanId,
    resetCurrentPlanId,
    comparedPlanId,
    updateComparedPlanId,
    resetComparedPlanId,
  };

  return <PlanContext.Provider value={value}>{children}</PlanContext.Provider>;
};
